import { usePerms } from 'contexts/PermsContext';
import { useState } from 'react';
import { Button, Container } from 'react-bootstrap';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import {
  useCreateGroupMutation,
  useDeleteGroupMutation,
  useEditGroupMutation,
  useGetGroupsQuery,
} from 'services/devices/groups/groups.api';
import { AddEditGroupModal, DeleteGroupModal, GroupDeviceSettingsModal } from '../Modals';
import Accordion from './components/Accordion';
import Table from './components/Table';
import { buildGroupPayload } from './utils/groupUtils';

export default function ConnectedDeviceGroups() {
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const { data: deviceGroups = [] } = useGetGroupsQuery();
  const [createGroup] = useCreateGroupMutation();
  const [editGroup] = useEditGroupMutation();
  const [deleteGroup] = useDeleteGroupMutation();
  const perms = usePerms();

  // Modal state: -2 closed, -1 add, >=0 edit by group id.
  const [modalOpen, setModalOpen] = useState(-2);
  const [toDelete, setToDelete] = useState(null);
  const [settingsGroupId, setSettingsGroupId] = useState(null);

  const onAddGroup = () => setModalOpen(-1);

  const onEditGroup = (id) => setModalOpen(id);

  const onDeleteGroup = (group) => setToDelete(group);

  const onOpenGroupSettings = (groupId) => setSettingsGroupId(groupId);
  const onCloseGroupSettings = () => setSettingsGroupId(null);

  const resolveDelete = async (success) => {
    try {
      if (success && toDelete) {
        await deleteGroup(toDelete.id).unwrap();
      }
    } catch (error) {
      console.error('Failed to delete group.');
    }

    setToDelete(null);
  };

  const resolveAddEditGroup = async (formData) => {
    const finalData = buildGroupPayload(formData, connectedDevices);

    try {
      if (modalOpen === -1) {
        await createGroup({ ...finalData }).unwrap();
      } else if (modalOpen >= 0) {
        await editGroup({ ...finalData }).unwrap();
      } else {
        return;
      }
    } catch (error) {
      console.error('Failed to add/edit group.');
    }

    setModalOpen(-2);
  };

  const rejectAddEditGroup = () => setModalOpen(-2);

  const hasDeletePermission = perms.testPermission([8, 9, 10, 11, 12, 13], ['DeleteDeviceGroups']);

  const hasCreateEditPermission = perms.testPermission(
    [8, 9, 10, 11, 12, 13],
    ['Create-EditDeviceGroups'],
  );

  const settingsGroup = deviceGroups?.find((group) => group.id === settingsGroupId);

  return (
    <Container fluid className="d-flex flex-column gap-3">
      <Table
        hasCreateEditPermission={hasCreateEditPermission}
        hasDeletePermission={hasDeletePermission}
        onEditGroup={onEditGroup}
        onDeleteGroup={onDeleteGroup}
        onOpenGroupSettings={onOpenGroupSettings}
      />

      <Accordion
        hasCreateEditPermission={hasCreateEditPermission}
        hasDeletePermission={hasDeletePermission}
        onEditGroup={onEditGroup}
        onDeleteGroup={onDeleteGroup}
        onOpenGroupSettings={onOpenGroupSettings}
      />

      {hasCreateEditPermission && (
        <div className="d-flex justify-content-center justify-content-lg-start w-100 mt-3">
          <Button className="w-100 w-md-3xs py-3 py-md-4" variant="primary" onClick={onAddGroup}>
            New Device Group
          </Button>
        </div>
      )}

      <AddEditGroupModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        resolveAddEditGroup={resolveAddEditGroup}
        rejectAddEditGroup={rejectAddEditGroup}
      />
      <DeleteGroupModal toDelete={toDelete} resolveDelete={resolveDelete} />
      <GroupDeviceSettingsModal
        show={!!settingsGroup}
        onClose={onCloseGroupSettings}
        group={settingsGroup}
      />
    </Container>
  );
}
